// src/components/Header.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DropdownMenu from './common/DropdownMenu';

// 👉 햄버거로 전환할 기준(태블릿 포함)
const MENU_BREAK = 1024;

const navItems = [
  {
    title: 'ABOUT',
    sub: ['CEO 인사말', '연혁', '인증/수상'],
    path: '/about/ceo',
    subPaths: ['/about/ceo', '/about/history', '/about/certifications'],
  },
  {
    title: 'PRODUCTS',
    sub: [
      '도로 안전경보시스템',
      '도로 안전유도시설',
      '도로환경 개선시설',
      '도로 안내표지시스템',
    ],
    path: '/products/traffic-safety',
    subPaths: [
      '/products/traffic-safety',
      '/products/guardrail',
      '/products/facilities',
      '/products/signage',
    ],
  },
  {
    title: 'ESG',
    sub: ['ESG 경영', '제보센터'],
    path: '/esg/esg',
    subPaths: ['/esg/esg', '/esg/report'],
  },
  {
    title: 'COMMUNITY',
    sub: ['뉴스', '문의하기'],
    path: '/community/news',
    subPaths: ['/community/news', '/community/contact'],
  },
];

const HeaderContainer = styled.header`
  position: fixed;
  top: 0; left: 0; right: 0;
  background: ${props => (props.scrolled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0,0,0,0.5)')};
  z-index: 1000;
  transition: all 0.3s ease;
  padding: 0.6rem 0;
  border-bottom: ${p => (p.scrolled ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.05)')};
  box-shadow: ${p => (p.scrolled ? '0 2px 20px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)')};
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 3rem;
  position: relative;

  @media (max-width: ${MENU_BREAK}px) {
    padding: 0 1.5rem;
    justify-content: space-between;
  }
`;

const LogoContainer = styled.div`
  position: absolute;
  left: 3rem;
  cursor: pointer;
  

  @media (max-width: ${MENU_BREAK}px) {
    position: static;
    left: auto;
  }
`;

const LogoImage = styled.img`
  --h: 120px;
  height: ${p => (p.scrolled ? 'calc(var(--h) * 0.95)' : 'var(--h)')};
  width: auto;
  transition: height .25s ease, filter .3s ease, transform .15s ease;
  filter: ${p => (p.scrolled ? 'brightness(1)' : 'brightness(1.1)')};
  padding-top: 1.8rem;
  padding-bottom: 1.5rem;

  &:hover {
    transform: scale(1.03);
    filter: brightness(1.15);
  }

  @media (max-width: 768px)  { --h: 86px; padding: 0.25rem 0;}
  @media (max-width: 480px)  { --h: 64px; padding: 0.25rem 0;}
`;

const LogoFallback = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -1px;

  span {
    color: ${props => (props.scrolled ? '#ffffff' : '#60a5fa')};
    transition: color .3s;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 4rem;

  /* ▼ 모바일/태블릿(<=1024)에서는 토글 패널 */
  @media (max-width: ${MENU_BREAK}px) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0; right: 0;
    background: rgba(0, 0, 0, 0.95);
    backdrop-filter: blur(20px);
    flex-direction: column;
    padding: 2rem 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    gap: 0.75rem;
    max-height: calc(100dvh - 70px);
    overflow-y: auto;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  font-size: 1.7rem;
  color: #ffffff;
  line-height: 1;

  @media (max-width: ${MENU_BREAK}px) {
    display: block;
  }
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // 👉 브레이크포인트 기준을 JS에서도 동일하게 사용
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MENU_BREAK);

  useEffect(() => {
    const onResize = () => {
      const nowMobile = window.innerWidth <= MENU_BREAK;
      setIsMobile(nowMobile);
      // 데스크탑으로 올라갈 때는 패널 닫기(중구난방 방지)
      if (!nowMobile) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize, { passive: true });
    onResize(); // 초기 1회 동기화
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogoClick = () => { window.location.href = '/'; };
  const handleImageError = () => setLogoError(true);

  // 모바일에서 항목 클릭 시 패널 닫기
  const handleNavigate = () => { if (isMobile) setMenuOpen(false); };

  return (
    <HeaderContainer id="site-header" scrolled={scrolled}>
      <Nav>
        <LogoContainer onClick={handleLogoClick}>
          {!logoError ? (
            <LogoImage
              // src="/images/logo/seyeon_logo_blue.png"
              src="/images/logo/seyeon_logo.png"
              alt="SEYEON ENS"
              scrolled={scrolled}
              onError={handleImageError}
            />
          ) : (
            <LogoFallback scrolled={scrolled}>
              SEYEON<span>ENS</span>
            </LogoFallback>
          )}
        </LogoContainer>

        <NavMenu isOpen={menuOpen}>
          {navItems.map((item) => (
            <DropdownMenu
              key={item.title}
              item={item}
              isMobile={isMobile}          // 필요 시 모바일에서 클릭 동작으로 처리
              onNavigate={handleNavigate}  // 항목 클릭 후 닫기
            />
          ))}
        </NavMenu>

        <MenuToggle onClick={() => setMenuOpen(o => !o)}>
          {menuOpen ? '✕' : '☰'}
        </MenuToggle>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
